import { useEffect, useRef, useState, type ReactNode } from "react";
import { bedienerPflicht } from "@mrmachine/fach";
import { einheitFuer, useT, type Uebersetzer } from "../i18n";
import { Erklaerung, Marke, euro } from "./index";
import { Betragssatz } from "./Betragssatz";
import { Zeitraum, type Spanne } from "./Zeitraum";
import { ARTWORT } from "./Positionen";
import { Leistungsfelder, mengeFolgtAusAufmass, mengentext, mitMengenfeld } from "./Leistungsmenge";
import { betragEingabe, betragFeldtext, betragVerlassen } from "../daten/zahlfeld";
import type { Preisauskunft } from "../daten/typen";
import {
  alsMerkzuschlag, gerechneterSatz, istMiete, mietsatz, postenSumme, preisFehlt, zuschlagNetto,
  zuschlaegeZusammenfuehren, zuschlagsteile, type Merkposten, type Merkzuschlag, type Teilposten,
} from "../daten/merkliste";
/* Bis 08.10.2026 brachte die Zuschlagsanzeige dieses Blatt mit; davon lebt
   noch `.pa-zuschlag-marke` (Blatt der Miete im Vorgang). Es bleibt geladen,
   bis die Regel dort ein eigenes Zuhause hat — siehe docs/64 „Merkliste". */
import "../stile/auskunft.css";

/* ══════════════════════════════════════════════════════════════════
   Ein Posten, aufgeklappt — Einstellungen und Aufstellung (08.10.2026).

   Luca: *„In der Schnellvermietung rechts die Merkliste: Die einzelnen
   Positionen sind farblich komisch. Wenn ich die aufklicke, ist das alles
   grau und hat keine Struktur. Bei ‚Maschinenbruchversicherung‘ ist das
   nicht sauber möglich. … Wenn ich ‚Jetzt vermieten‘ mache, kann ich auch
   nicht in die Details der einzelnen Positionen gehen."*

   **Ein Baustein für drei Orte:** die aufgeklappte Zeile der Merkliste
   (`Merkliste.tsx`, `Sammelkarte`), die aufgeklappte Zeile im Abschluss
   (`MerklisteAbschluss.tsx`, `Postenzeile`) und das Blatt „Details" am
   Schreibtisch (`Postenblatt.tsx`). Keine zweite Fassung — wer dort einen
   Zuschlag abwählt, sieht hier dasselbe.

   **Vorbild ist die Auskunft der Gerätezeile** (`Trefferauskunft.tsx`,
   `AuskunftTafel`, docs/64 „Vermieten, Auskunft"): eine Tabelle mit festen
   Spalten Bezeichnung · Menge · Einzel · Betrag, die Miete zuerst, darunter
   leise der Gruppentitel „Zuschläge", dann die Summe. Anders als dort ist
   hier jede Zeile **bedienbar**: der Haken vor dem Zuschlag nimmt ihn
   heraus (die Zeile bleibt durchgestrichen stehen), das Feld in „Einzel"
   sagt einen anderen Satz zu. Der Betrag bleibt dabei sichtbar — bis
   08.10.2026 ersetzte im Blatt das Feld den Betrag, und man sah nicht mehr,
   was der Zuschlag kostet.

   **Gerechnet wird nicht neu.** Die Zeilen lesen dieselben Werte wie die
   Summe (`mietsatz`, `zuschlagNetto`, `zuschlagsteile`, `postenSumme`), und
   die Summe des Postens ist die Summe der Zeilen. Mit `frisch` rechnet die
   Tafel mit der frisch geholten Preisantwort — dieselbe Zusammenführung wie
   die Abschlusszeile (`zuschlaegeZusammenfuehren`), damit Kopf und Tafel
   dort dieselbe Zahl nennen.

   Klassen: `pst-*` (`stile/merkliste.css`), gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/** „5 Tage", „1 Pauschale", „580 m²" — Zahl und Einheit in einer Zelle. */
function mengeMitEinheit(t: Uebersetzer, menge: number, einheitKey: string | undefined): string {
  const zahl = mengentext(menge);
  return einheitKey ? `${zahl} ${einheitFuer(t, einheitKey, menge)}` : zahl;
}

const rund = (n: number): number => Math.round(n * 100) / 100;

/**
 * Ein Betragsfeld in der Spalte „Einzel" — der Text gehört dem, der tippt
 * (N4-01), geschrieben wird beim Verlassen (N5-02, `betragVerlassen`), eine
 * dritte Stelle bleibt rot stehen und schreibt nichts (N4-06).
 */
function Einzelfeld({ schild, wert, platz, setzen }: {
  schild: string;
  /** Der zugesagte Satz — leer heißt „der gerechnete gilt". */
  wert: number | undefined;
  /** Im leeren Feld: der gerechnete Satz bzw. „Preis eingeben". */
  platz: string;
  setzen: (zahl: number | undefined) => void;
}) {
  const [text, setzeText] = useState(wert === undefined ? "" : betragFeldtext(wert));
  const drin = useRef(false);
  useEffect(() => {
    if (!drin.current) setzeText(wert === undefined ? "" : betragFeldtext(wert));
  }, [wert]);
  const fehler = betragEingabe(text).fehler;
  return (
    <span className="pst-einzel-feld">
      <input
        className="feld pst-feld-zahl"
        inputMode="decimal"
        autoComplete="off"
        aria-label={schild}
        aria-invalid={fehler !== null}
        value={text}
        placeholder={platz}
        onFocus={() => { drin.current = true; }}
        onChange={(e) => setzeText(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); }}
        onBlur={(e) => {
          drin.current = false;
          const raus = betragVerlassen(e.target.value, wert);
          setzeText(raus.text);
          if (raus.schreiben) setzen(raus.zahl ?? undefined);
        }}
      />
      <Betragssatz text={text} />
    </span>
  );
}

/** Eine Zeile der Tafel — vier Zellen, die erste mit Haken und Nebentext. */
function Tafelzeile({ name, neben, nebenArt, haken, menge, einzel, betrag, klasse = "" }: {
  name: ReactNode;
  neben?: ReactNode;
  /** Nebentext als Warnung („nicht abgestimmt") — Wort und Farbe, nie Farbe allein. */
  nebenArt?: "acht" | undefined;
  haken?: ReactNode;
  menge: ReactNode;
  einzel: ReactNode;
  betrag: ReactNode;
  klasse?: string;
}) {
  return (
    <div className={`pst-zeile${klasse ? ` ${klasse}` : ""}`} role="row">
      <span className="pst-was" role="rowheader">
        {haken}
        <span className="pst-was-text">
          <span className="pst-name">{name}</span>
          {neben ? <span className={`pst-neben${nebenArt ? ` ${nebenArt}` : ""}`}>{neben}</span> : null}
        </span>
      </span>
      <span className="pst-zahl" role="cell">{menge}</span>
      <span className="pst-zahl pst-einzel" role="cell">{einzel}</span>
      <span className="pst-zahl pst-betrag" role="cell">{betrag}</span>
    </div>
  );
}

export function Postenaufstellung({ p, preis, frisch = false, nicht, aendern }: {
  p: Merkposten;
  /** Die Preisantwort zum Posten, wenn sie da ist — Einheiten und Sätze der Zuschläge. */
  preis?: Preisauskunft | undefined;
  /** Mit der frischen Antwort rechnen statt mit dem gemerkten Stand (Abschluss, Blatt). */
  frisch?: boolean;
  /** Codes „je Auftrag", die schon an einem anderen Posten zählen (V-20). */
  nicht?: ReadonlySet<string> | undefined;
  /** Fehlt es, ist die Tafel zum Lesen. */
  aendern?: ((teil: Teilposten) => void) | undefined;
}) {
  const t = useT();
  const trenn = ` ${t("allgemein.trennpunkt")} `;
  const miete = istMiete(p);
  const mitPreis = frisch && preis !== undefined;

  /* ── Die Miete bzw. die Leistung ── */
  const einheitKey = mitPreis ? preis.einheit_key : p.einheitKey;
  const einheiten = mitPreis ? preis.menge : p.einheiten;
  const satzGerechnet = mitPreis ? preis.einzelpreis : gerechneterSatz(p);
  const mieteJeStueck = mitPreis
    ? (p.einzelpreis === undefined ? preis.netto : rund(p.einzelpreis * preis.menge))
    : mietsatz(p);
  const gerechnet = p.gerechnet || mitPreis;

  /* ── Die Zuschläge — mit dem, was ein Mensch daran entschieden hat ── */
  const zuschlaege: Merkzuschlag[] = miete && mitPreis
    ? zuschlaegeZusammenfuehren(
      p.zuschlaege,
      preis.zuschlaege.filter((z) => z.vorgewaehlt).map(alsMerkzuschlag),
    )
    : p.zuschlaege;
  const preisZuschlag = (code: string) => preis?.zuschlaege.find((z) => z.code === code);
  const zuschlagsfrei = mitPreis ? preis.zuschlagsfrei === true : p.zuschlagsfrei === true;

  /* ── Die Summe ist die Summe der Zeilen — dieselbe Rechnung wie überall ── */
  const anbau = p.anbauNetto ?? 0;
  const summe = mitPreis
    ? (() => {
      const teile = zuschlagsteile(zuschlaege, nicht);
      return rund((mieteJeStueck + teile.jeStueck + anbau) * p.menge + teile.einmal);
    })()
    : postenSumme(p, nicht);
  const offen = zuschlaege.filter(preisFehlt).map((z) => z.bezeichnung);

  const zuschlagAendern = (code: string, neu: { aus?: boolean; einzelpreis?: number | undefined }) => {
    if (!aendern) return;
    aendern({
      zuschlaege: zuschlaege.map((z) => {
        if (z.code !== code) return z;
        /* Neu gesetzt wird nur, was die Wahl ändert; `faelltBeiAbholung`,
           `platzhalter` und der Rest bleiben (19.09.2026). Wer von Hand
           wieder anwählt, hebt die Abwahl des Schalters „Kunde holt ab" auf. */
        const { aus: _aus, einzelpreis: _satz, ausWegenAbholung: _grund, ...rest } = z;
        const aus = neu.aus ?? z.aus === true;
        const satz = "einzelpreis" in neu ? neu.einzelpreis : z.einzelpreis;
        return {
          ...rest,
          ...(aus ? { aus: true as const } : {}),
          ...(aus && z.ausWegenAbholung && neu.aus === undefined ? { ausWegenAbholung: true as const } : {}),
          ...(satz === undefined ? {} : { einzelpreis: satz }),
        };
      }),
    });
  };

  /* Die erste Zeile: Miete, sonst die Art der Zeile („Leistung"). */
  const ersteName = miete ? t("merkliste.aufstellung.miete") : t(ARTWORT[p.art] ?? "");
  const ersteMenge = !gerechnet
    ? ""
    : miete
      ? (einheiten ? mengeMitEinheit(t, einheiten, einheitKey) : "")
      : mengeFolgtAusAufmass(p)
        ? t("merkliste.aufstellung.aufmass")
        : mengeMitEinheit(t, p.menge, p.einheitKey);
  const ersteNeben = [
    miete && p.menge > 1 ? t("merkliste.aufstellung.jeStueck", { zahl: p.menge }) : "",
    p.einzelpreis !== undefined ? t("merkliste.aufstellung.vonHand", { betrag: euro(satzGerechnet) }) : "",
  ].filter(Boolean).join(trenn);
  const ersteBetrag = !gerechnet
    ? t("merkliste.preisOffen")
    : euro(miete ? rund(mieteJeStueck * p.menge) : rund(mietsatz(p) * p.menge));

  return (
    <div className="pst">
      <div className="pst-tafel" role="table" aria-label={t("merkliste.aufstellung.titel")}>
        <div className="pst-zeile pst-kopf" role="row">
          <span role="columnheader">{t("nachschlagen.auskunft.spalteWas")}</span>
          <span role="columnheader">{t("nachschlagen.auskunft.spalteMenge")}</span>
          <span role="columnheader">{t("nachschlagen.auskunft.spalteEinzel")}</span>
          <span role="columnheader">{t("nachschlagen.auskunft.spalteBetrag")}</span>
        </div>

        <Tafelzeile
          klasse="pst-erste"
          name={ersteName}
          neben={ersteNeben || undefined}
          menge={ersteMenge}
          einzel={aendern && gerechnet ? (
            <Einzelfeld
              schild={t("merkliste.satzSchild")}
              wert={p.einzelpreis}
              platz={betragFeldtext(satzGerechnet)}
              setzen={(zahl) => {
                /* Geleert heißt „wieder der gerechnete"; ein Betrag, der
                   zufällig derselbe ist, ist kein Preis von Hand — sonst
                   friert die Annahme einen Preis ein, den niemand gesetzt hat. */
                aendern({ einzelpreis: zahl === undefined || zahl === satzGerechnet ? undefined : zahl });
              }}
            />
          ) : gerechnet ? euro(p.einzelpreis ?? satzGerechnet) : ""}
          betrag={ersteBetrag}
        />

        {miete && (zuschlaege.length || zuschlagsfrei) ? (
          <div className="pst-gruppe" role="row">
            <span role="cell">
              {t("merkliste.detailsZuschlaege")}
              <Erklaerung k="start.preisAusStammWas" />
            </span>
          </div>
        ) : null}

        {miete && zuschlagsfrei ? (
          <Tafelzeile
            name={t("start.preisOhneZuschlaege")}
            menge=""
            einzel=""
            betrag={euro(0)}
          />
        ) : null}

        {miete && !zuschlagsfrei ? zuschlaege.map((z) => {
          const pz = preisZuschlag(z.code);
          const drin = !z.aus;
          const fehlt = preisFehlt(z);
          const menge = z.menge ?? pz?.menge ?? 1;
          const satzStamm = pz?.einzelpreis ?? (menge ? rund(z.netto / menge) : z.netto);
          const schonDa = z.jeAuftrag === true && nicht?.has(z.code) === true;
          const betrag = !drin || schonDa ? 0 : rund(zuschlagNetto(z) * (z.jeAuftrag ? 1 : p.menge));
          const neben = !drin
            ? t(z.ausWegenAbholung ? "merkliste.detailsEntfaelltAbholung" : "merkliste.detailsEntfaellt")
            : schonDa
              ? t("merkliste.aufstellung.schonGezaehlt")
              : fehlt
                ? t("zuschlaege.preisIndividuell")
                : z.platzhalter
                  ? t("preise.platzhalter")
                  : z.jeAuftrag
                    ? t("merkliste.aufstellung.einmalJeAuftrag")
                    : z.einzelpreis !== undefined
                      ? t("merkliste.aufstellung.vonHand", { betrag: euro(satzStamm) })
                      : "";
          return (
            <Tafelzeile
              key={z.code}
              klasse={drin ? "" : "aus"}
              haken={aendern ? (
                <input
                  type="checkbox"
                  className="pst-haken"
                  checked={drin}
                  aria-label={t("merkliste.aufstellung.zuschlagAn", { was: z.bezeichnung })}
                  onChange={(e) => zuschlagAendern(z.code, { aus: !e.target.checked })}
                />
              ) : undefined}
              name={z.bezeichnung}
              neben={neben || undefined}
              nebenArt={drin && z.platzhalter && !fehlt ? "acht" : undefined}
              menge={mengeMitEinheit(t, menge, pz?.einheit_key)}
              einzel={aendern && drin ? (
                <Einzelfeld
                  schild={t("merkliste.aufstellung.satzSchild", { was: z.bezeichnung })}
                  wert={z.einzelpreis}
                  platz={fehlt || z.preisIndividuell ? t("merkliste.aufstellung.preisPlatz") : betragFeldtext(satzStamm)}
                  setzen={(zahl) => zuschlagAendern(z.code, { einzelpreis: zahl })}
                />
              ) : fehlt ? "" : euro(z.einzelpreis ?? satzStamm)}
              betrag={drin && fehlt
                ? <Marke art="stopp">{t("zuschlaege.preisFehlt")}</Marke>
                : schonDa ? t("allgemein.strich") : euro(betrag)}
            />
          );
        }) : null}

        {/* Die Anbauteile mit eigenem Tarif (0078) — gerechnet von
            `Anbauteile.tsx`, hier nur die Zeile, damit die Summe aufgeht. */}
        {anbau > 0 ? (
          <Tafelzeile
            name={t("merkliste.aufstellung.anbau")}
            neben={p.menge > 1 ? t("merkliste.aufstellung.jeStueck", { zahl: p.menge }) : undefined}
            menge=""
            einzel={euro(anbau)}
            betrag={euro(rund(anbau * p.menge))}
          />
        ) : null}

        {gerechnet ? (
          <Tafelzeile
            klasse="pst-summe"
            name={t("merkliste.aufstellung.summe")}
            neben={offen.length ? t("nachschlagen.auskunft.zzgl", { was: offen.join(", ") }) : undefined}
            menge=""
            einzel=""
            betrag={euro(summe)}
          />
        ) : null}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   Die Einstellungen eines Postens — ein Formular im Raster.

   Zeitraum, Stück, Bediener (Miete); Menge und Grad (Leistung); Stück
   (Ware, Zuschlag). Felder 40 px mit der Feldkante, die Beschriftung leise
   darüber; am Telefon 44 px und 16 px (`stile/merkliste.css`). Geändert
   wird über die Griffe des Aufrufers — die Merkliste prüft „frei?" und
   rechnet nach, der Abschluss rechnet frisch.
   ══════════════════════════════════════════════════════════════════ */

/** Wie viele Stück eine Zeile höchstens trägt. Darüber ist es ein Angebot. */
export const MENGE_HOECHSTENS = 20;

export function Posteneinstellungen({ p, zeitraumSetzen, stueckSetzen, bedientSetzen, kopf, zeitraumMarke }: {
  p: Merkposten;
  /** Unter dem Zeitraum: „eigener Zeitraum · wie alle" im Abschluss (08.10.2026). */
  zeitraumMarke?: ReactNode;
  /** Ohne: kein Zeitraumfeld (eine Leistung trägt ihren Termin im Abschluss). */
  zeitraumSetzen?: ((neu: Spanne) => void) | undefined;
  stueckSetzen: (menge: number) => void;
  bedientSetzen?: ((bedient: boolean) => void) | undefined;
  /** Was rechts neben dem Gruppentitel steht — etwa „Details". */
  kopf?: ReactNode;
}) {
  const t = useT();
  const miete = istMiete(p);
  const leistung = p.art === "leistung";
  const pflicht = p.bedienungspflicht ?? "wahlweise";
  const mussBedient = bedienerPflicht(pflicht);
  const nieBedient = pflicht === "ohne_person";
  const zeitraumDa = Boolean(zeitraumSetzen) && (miete || leistung);
  /* Leistungen zählen in ihrer Einheit (Leistungsfelder); eine Pauschale
     ohne Mengenfeld und jede Ware oder jeder Zuschlag zählt Stück. */
  const stueckDa = p.art !== "text" && !mitMengenfeld(p);

  return (
    <section className="pst-teil">
      <div className="pst-teilkopf">
        <span className="pst-titel">{t("merkliste.aufstellung.einstellungen")}</span>
        {kopf}
      </div>
      <div className="pst-raster">
        {zeitraumDa ? (
          <div className="pst-zelle breit">
            <span className="pst-schild">
              {t(miete ? "merkliste.aufstellung.zeitraum" : "merkliste.aufstellung.termin")}
            </span>
            <Zeitraum
              id={`pst-zeit-${p.id}`}
              wert={{ von: p.von || null, bis: p.bis || null }}
              schild={t(miete ? "merkliste.aufstellung.zeitraum" : "merkliste.aufstellung.termin")}
              /* Ohne Kalendertage im Feld: Die Tafel darunter nennt die
                 Miettage, und „7 Tage" neben „5 Tage" läse sich wie ein Widerspruch. */
              ohneTage
              /* Erst der ganze Zeitraum geht an den Posten — der angefangene
                 bleibt im Feld (08.10.2026, „der Datepicker funktioniert nicht"). */
              ganz
              aendern={(neu) => zeitraumSetzen?.(neu)}
            />
            {zeitraumMarke}
          </div>
        ) : null}

        {leistung ? <Leistungsfelder p={p} /> : null}

        {stueckDa ? (
          <div className="pst-zelle">
            <span className="pst-schild">{t("merkliste.stueck")}</span>
            <span className="pst-stueck">
              <button
                type="button"
                className="pst-schritt"
                aria-label={t("merkliste.wenigerSchild")}
                disabled={p.menge <= 1}
                onClick={() => stueckSetzen(Math.max(1, p.menge - 1))}
              >
                −
              </button>
              <b className="pst-stueck-zahl" aria-live="polite">{mengentext(p.menge)}</b>
              <button
                type="button"
                className="pst-schritt"
                aria-label={t("merkliste.mehrSchild")}
                disabled={p.menge >= MENGE_HOECHSTENS}
                onClick={() => stueckSetzen(Math.min(MENGE_HOECHSTENS, p.menge + 1))}
              >
                +
              </button>
            </span>
          </div>
        ) : null}

        {miete && !nieBedient ? (
          <div className="pst-zelle">
            <span className="pst-schild">{t("schnell.bedienung")}</span>
            {/* Holt der Kunde ab, steht „mit Bediener" nicht zur Wahl (08.10.2026, wie im Angebot). */}
            {mussBedient || !bedientSetzen || p.selbstabholung ? (
              <span className="pst-wert">
                {mussBedient && p.selbstabholung
                  ? t("positionen.pflichtGegenAbholung")
                  : p.bediener
                    ? p.bediener
                    : mussBedient ? t("merkliste.bedienerOffenKurz")
                      : p.selbstabholung ? t("merkliste.ohneBedienerAbholung")
                        : t(p.bedient ? "schnell.mitBediener" : "schnell.ohneBediener")}
              </span>
            ) : (
              <select
                className="feld"
                aria-label={t("schnell.bedienung")}
                value={p.bedient ? "mit" : "ohne"}
                onChange={(e) => bedientSetzen(e.target.value === "mit")}
              >
                <option value="ohne">{t("schnell.ohneBediener")}</option>
                <option value="mit">
                  {p.bediener ? t("merkliste.aufstellung.mitBedienerName", { name: p.bediener }) : t("schnell.mitBediener")}
                </option>
              </select>
            )}
          </div>
        ) : null}
      </div>
    </section>
  );
}

/** Der leise Gruppentitel über einem Block der Aufklappung. */
export function Postenteil({ titel, children }: { titel: string; children: ReactNode }) {
  return (
    <section className="pst-teil">
      <div className="pst-teilkopf"><span className="pst-titel">{titel}</span></div>
      {children}
    </section>
  );
}
